import React, { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { AvatarItem } from '@atlaskit/avatar';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss, Flex } from '@atlaskit/primitives';

import { ExternalCollaboratorUserLozenge } from '@confluence/external-collab-ui/entry-points/ExternalUserLozengeLoader.ts';
import { ProfileAvatar } from '@confluence/profile/entry-points/ProfileAvatarComponent.ts';

import type { Account } from '../../../hooks/index.ts';
import { AccountType } from '../../../hooks/index.ts';
import { getLastViewedText } from '../../analyticsUtils.ts';
import { AvatarSkeleton, PrimaryTextSkeleton, SecondaryTextSkeleton } from '../LoadingSkeleton/index.ts';
import { EngagementCell } from '../../PageAnalytics/EngagementCell.tsx';

import { UserListRow } from './userListStyled.tsx';

const i18n = defineMessages({
	anonymous: {
		id: 'confluence-analytics.dialog.user-list.anonymous-user-name',
		defaultMessage: 'Anonymous',
		description: 'Text for an anonymous user in a list of users.',
	},
	deactivated: {
		id: 'confluence-analytics.dialog.user-list.deactivated-user-label',
		defaultMessage: 'Deactivated',
		description: 'Label indicating that a user is no longer active.',
	},
});

const EngagementLozengeSkeletonStyles = xcss({
	width: '80px',
	height: '24px',
	backgroundColor: 'color.skeleton',
});

const EngagementLozengeStyles = xcss({
	width: '120px',
	textAlign: 'right',
});

const GuestAvatarStyles = xcss({
	width: '200px',
});

const DefaultAvatarStyles = xcss({
	width: '260px',
});

const UserContainerStyles = xcss({
	marginLeft: 'space.negative.075',
});

export type AnalyticsDialogUserListRowProps =
	| {
			isLoading: true;
			account?: undefined;
	  }
	| {
			isLoading: false;
			account: Account;
	  };

export const AnalyticsDialogUserListRow = ({
	isLoading,
	account,
}: AnalyticsDialogUserListRowProps) => {
	const intl = useIntl();

	const displayName = useMemo(() => {
		if (isLoading) {
			return null;
		}
		if (account.accountType === AccountType.ANONYMOUS) {
			return intl.formatMessage(i18n.anonymous);
		}
		if (account.isDeactivated) {
			return `${account.name} (${intl.formatMessage(i18n.deactivated)})`;
		}
		return account.name;
	}, [account?.accountType, account?.isDeactivated, account?.name, intl, isLoading]);

	const secondaryText = useMemo(() => {
		if (isLoading) {
			return null;
		}
		if (!account?.lastViewedAt) {
			return null;
		}
		return getLastViewedText(new Date(account.lastViewedAt), intl);
	}, [account?.lastViewedAt, intl, isLoading]);

	const engagementLozenge = useMemo(() => {
		if (isLoading) {
			return null;
		}
		return (
			<EngagementCell isEngaged={account?.isEngaged ?? null} lastViewDate={account.lastViewedAt} />
		);
	}, [account?.isEngaged, account?.lastViewedAt, isLoading]);

	const ariaLabel = useMemo(() => {
		const ariaLabelParts = [displayName, secondaryText].filter(Boolean);
		if (ariaLabelParts.length === 0) {
			return undefined;
		}
		return ariaLabelParts.join(', ');
	}, [displayName, secondaryText]);
	const name = displayName ?? '';
	const isGuest = account?.accountType === AccountType.GUEST;

	const avatarItem = useMemo(() => {
		return (
			<Box
				xcss={isGuest ? GuestAvatarStyles : DefaultAvatarStyles}
				testId="analytics-dialog-user-list-row-text"
			>
				<AvatarItem
					avatar={
						isLoading ? (
							<AvatarSkeleton />
						) : (
							<ProfileAvatar
								userId={account.id}
								src={account.avatarURL ?? ''}
								isAnonymous={account.accountType === AccountType.ANONYMOUS}
								canViewProfile
								size="small"
								name={name}
							/>
						)
					}
					primaryText={isLoading ? <PrimaryTextSkeleton /> : displayName}
					secondaryText={isLoading ? <SecondaryTextSkeleton /> : secondaryText}
					isTruncationDisabled={false}
				/>
			</Box>
		);
	}, [
		isLoading,
		account?.id,
		account?.avatarURL,
		account?.accountType,
		name,
		secondaryText,
		isGuest,
		displayName,
	]);

	return (
		<UserListRow aria-label={ariaLabel}>
			<Flex
				direction="row"
				alignItems="center"
				justifyContent="space-between"
				xcss={UserContainerStyles}
			>
				{avatarItem}
				{isGuest && <ExternalCollaboratorUserLozenge />}
				{isLoading ? (
					<Box
						xcss={EngagementLozengeSkeletonStyles}
						testId="analytics-dialog-engagement-lozenge-loading"
					/>
				) : (
					<Box xcss={EngagementLozengeStyles}>{engagementLozenge}</Box>
				)}
			</Flex>
		</UserListRow>
	);
};
